import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Bell, Send, Users, Building2, User, History,
  RefreshCw, Check, X, Loader2, Megaphone, AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Painel de Controle de Notificações para Admin
 * Permite enviar notificações em massa para campanhas e alertas
 */
const NotificationControlPanel = () => {
  const [stats, setStats] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  
  // Formulário de notificação
  const [titulo, setTitulo] = useState('');
  const [corpo, setCorpo] = useState('');
  const [tipoDestino, setTipoDestino] = useState('todos'); // todos, admin, fornecedor, afiliado
  const [url, setUrl] = useState('/');

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      
      // Carregar estatísticas
      const statsRes = await fetch(`${BACKEND_URL}/api/notifications/stats`);
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
      
      // Carregar histórico
      const histRes = await fetch(`${BACKEND_URL}/api/notifications/historico/admin?limit=20`);
      if (histRes.ok) {
        const data = await histRes.json();
        setHistorico(data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const enviarNotificacao = async () => {
    if (!titulo.trim() || !corpo.trim()) {
      toast.error('Preencha o título e o corpo da notificação');
      return;
    }

    try {
      setSending(true);
      
      const payload = {
        tipo_usuario: tipoDestino === 'todos' ? null : tipoDestino,
        notification: {
          titulo: titulo,
          corpo: corpo,
          url: url || '/',
          icone: '/logo192.png'
        }
      };

      const response = await fetch(`${BACKEND_URL}/api/notifications/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        toast.success(`✅ ${result.message}`);
        setTitulo('');
        setCorpo('');
        setUrl('/');
        carregarDados(); // Recarregar histórico
      } else {
        toast.error(result.message || 'Erro ao enviar notificação');
      }
    } catch (error) {
      toast.error('Erro ao enviar notificação');
    } finally {
      setSending(false);
    }
  };

  const templates = [
    {
      nome: '🎉 Promoção',
      titulo: '🎉 Promoção Especial!',
      corpo: 'Aproveite descontos exclusivos por tempo limitado!',
      url: '/'
    },
    {
      nome: '🔧 Manutenção',
      titulo: '🔧 Manutenção Programada',
      corpo: 'O sistema estará em manutenção das 00h às 06h. Obrigado pela compreensão.',
      url: '/'
    },
    {
      nome: '📢 Novidade',
      titulo: '📢 Nova Funcionalidade!',
      corpo: 'Confira as novidades que preparamos para você!',
      url: '/'
    },
    {
      nome: '⚠️ Alerta',
      titulo: '⚠️ Atenção!',
      corpo: 'Informação importante sobre sua conta.',
      url: '/'
    }
  ];

  const aplicarTemplate = (template) => {
    setTitulo(template.titulo);
    setCorpo(template.corpo);
    setUrl(template.url);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-orange-500" />
          <h2 className="text-lg font-bold text-white">Central de Notificações</h2>
        </div>
        <Button
          onClick={carregarDados}
          disabled={loading}
          variant="outline"
          size="sm"
          className="border-gray-700"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-blue-500/10 border-blue-500/30 p-3">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-400" />
              <div>
                <p className="text-gray-400 text-xs">Total Inscritos</p>
                <p className="text-blue-400 text-xl font-bold">{stats.total_subscriptions}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30 p-3">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-red-400" />
              <div>
                <p className="text-gray-400 text-xs">Admins</p>
                <p className="text-red-400 text-xl font-bold">{stats.por_tipo?.admin || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30 p-3">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-400" />
              <div>
                <p className="text-gray-400 text-xs">Fornecedores</p>
                <p className="text-purple-400 text-xl font-bold">{stats.por_tipo?.fornecedor || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/30 p-3">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-orange-400" />
              <div>
                <p className="text-gray-400 text-xs">Afiliados</p>
                <p className="text-orange-400 text-xl font-bold">{stats.por_tipo?.afiliado || 0}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Formulário de Envio */}
      <Card className="bg-gray-800/50 border-gray-700 p-4">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2">
          <Send className="w-5 h-5 text-green-400" />
          Enviar Nova Notificação
        </h3>

        {/* Templates Rápidos */}
        <div className="flex flex-wrap gap-2 mb-4">
          {templates.map((template, idx) => (
            <Button
              key={idx}
              size="sm"
              variant="outline"
              className="border-gray-600 h-7 text-xs"
              onClick={() => aplicarTemplate(template)}
            >
              {template.nome}
            </Button>
          ))}
        </div>

        <div className="space-y-3">
          {/* Título */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">Título</label>
            <Input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ex: 🎉 Promoção Especial!"
              className="bg-gray-900 border-gray-700"
              maxLength={60}
            />
            <p className="text-gray-500 text-xs mt-1">{titulo.length}/60 caracteres</p>
          </div>

          {/* Corpo */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">Mensagem</label>
            <textarea
              value={corpo}
              onChange={(e) => setCorpo(e.target.value)}
              placeholder="Digite a mensagem da notificação..."
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white resize-none"
              rows={3}
              maxLength={200}
            />
            <p className="text-gray-500 text-xs mt-1">{corpo.length}/200 caracteres</p>
          </div>

          {/* Destino */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">Enviar para</label>
            <div className="flex gap-2">
              {[
                { value: 'todos', label: 'Todos', icon: Users },
                { value: 'admin', label: 'Admins', icon: User },
                { value: 'fornecedor', label: 'Fornecedores', icon: Building2 },
                { value: 'afiliado', label: 'Afiliados', icon: User }
              ].map((opt) => (
                <Button
                  key={opt.value}
                  size="sm"
                  onClick={() => setTipoDestino(opt.value)}
                  className={`h-8 ${tipoDestino === opt.value ? 'bg-orange-500' : 'bg-gray-700'}`}
                >
                  <opt.icon className="w-3 h-3 mr-1" />
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          {/* URL (opcional) */}
          <div>
            <label className="text-gray-400 text-sm mb-1 block">URL de destino (opcional)</label>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/"
              className="bg-gray-900 border-gray-700"
            />
          </div>

          {/* Botão Enviar */}
          <Button
            onClick={enviarNotificacao}
            disabled={sending || !titulo.trim() || !corpo.trim()}
            className="w-full bg-green-500 hover:bg-green-600 h-10"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            {sending ? 'Enviando...' : 'Enviar Notificação'}
          </Button>
        </div>
      </Card>

      {/* Histórico */}
      <Card className="bg-gray-800/50 border-gray-700 p-4">
        <h3 className="text-white font-bold mb-3 flex items-center gap-2">
          <History className="w-5 h-5 text-blue-400" />
          Histórico de Envios
        </h3>

        {historico.length === 0 ? (
          <p className="text-gray-500 text-sm text-center py-4">
            Nenhuma notificação enviada ainda
          </p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {historico.map((notif, idx) => (
              <div
                key={idx}
                className="bg-gray-900 rounded-lg p-3 border border-gray-700"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">{notif.titulo}</p>
                    <p className="text-gray-400 text-xs mt-1">{notif.corpo}</p>
                  </div>
                  <div className="text-right ml-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      notif.enviadas > 0 ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-400'
                    }`}>
                      {notif.enviadas} enviada(s)
                    </span>
                    {notif.falhas > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400 ml-1">
                        {notif.falhas} falha(s)
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  {notif.created_at ? new Date(notif.created_at).toLocaleString('pt-BR') : 'Data não disponível'}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default NotificationControlPanel;
